/**
 * AdminMyJob — v49.7. Admin dashboard for MyJob:
 *   * Global list of all offers (delete if needed)
 *   * Revenue analytics from quota packs (day/week/month/all-time, gross + net Stripe fees)
 * i18n: 4 admin languages (fr, en, es, zh) via useAdminLang hook.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, Briefcase, Loader2, Trash2, TrendingUp } from 'lucide-react';
import Navigation from '../components/Navigation';
import { useAdminLang } from '../hooks/useAdminLang';

const A_L = {
  fr: {
    title: 'MyJob — Offres', empty: 'Aucune offre', delete: 'Supprimer',
    confirmDelete: 'Supprimer cette offre ?', rev: 'Revenus MyJob',
    day: "Aujourd'hui", week: '7 jours', month: '30 jours',
    total: 'Total (net après frais Stripe)', count: 'packs', gross: 'Brut',
    net: 'Net (après frais Stripe)', all_time: 'Tout temps',
    deleted: 'supprimé', apps: 'candidatures', errGeneric: 'Erreur', ok: 'OK',
  },
  en: {
    title: 'MyJob — Offers', empty: 'No offers', delete: 'Delete',
    confirmDelete: 'Delete this offer?', rev: 'MyJob revenue',
    day: 'Today', week: '7 days', month: '30 days',
    total: 'Total (net after Stripe fees)', count: 'packs', gross: 'Gross',
    net: 'Net (after Stripe fees)', all_time: 'All-time',
    deleted: 'deleted', apps: 'apps', errGeneric: 'Error', ok: 'OK',
  },
  es: {
    title: 'MyJob — Ofertas', empty: 'Sin ofertas', delete: 'Eliminar',
    confirmDelete: '¿Eliminar esta oferta?', rev: 'Ingresos MyJob',
    day: 'Hoy', week: '7 días', month: '30 días',
    total: 'Total (neto tras comisión Stripe)', count: 'packs', gross: 'Bruto',
    net: 'Neto (tras comisión Stripe)', all_time: 'Todo el tiempo',
    deleted: 'eliminado', apps: 'postulaciones', errGeneric: 'Error', ok: 'OK',
  },
  zh: {
    title: 'MyJob — 招聘信息', empty: '暂无招聘', delete: '删除',
    confirmDelete: '删除该招聘？', rev: 'MyJob 收入',
    day: '今日', week: '7天', month: '30天',
    total: '合计（扣除 Stripe 手续费后净收入）', count: '套餐', gross: '毛收入',
    net: '净收入（Stripe 手续费后）', all_time: '累计',
    deleted: '已删除', apps: '申请', errGeneric: '错误', ok: '成功',
  },
};

const AdminMyJob = () => {
  const navigate = useNavigate();
  const { adminLang } = useAdminLang();
  const l = A_L[adminLang] || A_L.fr;
  const [offers, setOffers] = useState([]);
  const [rev, setRev] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [o, r] = await Promise.all([
        axios.get('/api/myjob/admin/offers'),
        axios.get('/api/myjob/admin/revenues'),
      ]);
      setOffers(o.data.items || []);
      setRev(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || l.errGeneric); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line

  const del = async (id) => {
    if (!window.confirm(l.confirmDelete)) return;
    try { await axios.delete(`/api/myjob/admin/offers/${id}`); toast.success(l.ok); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || l.errGeneric); }
  };

  return (
    <div className="min-h-screen bg-black text-white pb-24" data-testid="admin-myjob-page">
      <header className="sticky top-0 z-40 bg-black/80 backdrop-blur border-b border-white/10 px-4 py-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-2 rounded-full hover:bg-white/10" data-testid="admin-myjob-back"><ArrowLeft className="w-5 h-5" /></button>
        <h1 className="text-lg font-bold flex items-center gap-2"><Briefcase className="w-5 h-5 text-amber-400" /> {l.title}</h1>
      </header>

      <main className="px-4 py-4 space-y-4">
        {/* Revenue block */}
        <section className="p-4 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-600/10 border border-amber-500/30" data-testid="admin-myjob-rev">
          <h3 className="text-sm font-bold flex items-center gap-2 mb-3"><TrendingUp className="w-4 h-4" /> {l.rev}</h3>
          {loading || !rev ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm">
              <Stat title={l.day} amount={rev.day?.revenue_eur} count={rev.day?.count} l={l} />
              <Stat title={l.week} amount={rev.week?.revenue_eur} count={rev.week?.count} l={l} />
              <Stat title={l.month} amount={rev.month?.revenue_eur} count={rev.month?.count} l={l} />
              <div className="p-2 rounded-lg bg-white/5">
                <p className="text-[10px] text-white/50">{l.all_time}</p>
                <p className="font-bold">{l.gross} : {(rev.all_time?.revenue_eur || 0).toFixed(2)} €</p>
                <p className="text-emerald-300 font-bold">{l.net} : {(rev.all_time?.net_after_stripe_eur || 0).toFixed(2)} €</p>
                <p className="text-[10px] text-white/50">{rev.all_time?.count || 0} {l.count}</p>
              </div>
            </div>
          )}
        </section>

        {/* Offers */}
        <section className="space-y-2">
          <h3 className="text-sm font-bold">{l.title} ({offers.length})</h3>
          {loading ? <Loader2 className="w-6 h-6 animate-spin mx-auto" /> : offers.length === 0 ? <p className="text-center text-white/40 py-6">{l.empty}</p> : (
            <div className="space-y-2">
              {offers.map(o => (
                <div key={o.id} className="p-3 rounded-2xl bg-white/5 border border-white/10 flex items-start gap-2" data-testid={`admin-myjob-row-${o.id}`}>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate">{o.title} {o.deleted && <span className="text-red-300 text-xs">· {l.deleted}</span>}</p>
                    <p className="text-xs text-white/50">{o.pro_username} · {o.contract_type} · {o.applications_count || 0} {l.apps}</p>
                  </div>
                  {!o.deleted && (
                    <button onClick={() => del(o.id)} className="p-2 rounded-lg hover:bg-red-500/20 text-red-300" data-testid={`admin-myjob-del-${o.id}`}><Trash2 className="w-4 h-4" /></button>
                  )}
                </div>
              ))}
            </div>
          )}
        </section>
      </main>
      <Navigation />
    </div>
  );
};

const Stat = ({ title, amount, count, l }) => (
  <div className="p-2 rounded-lg bg-white/5">
    <p className="text-[10px] text-white/50">{title}</p>
    <p className="font-bold">{(amount || 0).toFixed(2)} €</p>
    <p className="text-[10px] text-white/50">{count || 0} {l.count}</p>
  </div>
);

export default AdminMyJob;
